import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Activity } from "lucide-react";

export const ConstructionPanel = () => {
    const [data, setData] = useState(null);
    const [phase, setPhase] = useState("baugenehmigung");
    const [progress, setProgress] = useState(100);
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/construction");
            setData(data);
            setPhase(data.phase || "baugenehmigung");
            setProgress(data.progress ?? 0);
            setDescription(data.description || "");
        } catch (err) {
            toast.error("Konnte Bauphase nicht laden", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/construction", {
                phase, progress: parseInt(progress, 10), description,
            });
            setData(data);
            toast.success("Bauphase aktualisiert", {
                description: "Die Änderung ist sofort live auf der Website.",
            });
        } catch (err) {
            toast.error("Speichern fehlgeschlagen", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const setPhaseAndDefault = (newPhase) => {
        setPhase(newPhase);
        const found = (data?.phases || []).find((p) => p.value === newPhase);
        if (found && typeof found.default_progress === "number") {
            setProgress(found.default_progress);
        }
    };

    return (
        <div data-testid="admin-construction-panel">
            <div className="mb-6">
                <span className="overline">Bauphase</span>
                <h2 className="font-heading text-2xl text-ink mt-1">Echtzeit-Bauphasen-Tracker</h2>
                <p className="font-body text-sm text-stone-500 mt-1">
                    Aktualisiert die Anzeige auf der öffentlichen Website sofort.
                </p>
            </div>

            {loading ? (
                <div className="p-12 flex justify-center">
                    <Loader2 className="animate-spin text-stone-400" size={24} />
                </div>
            ) : (
                <div className="grid lg:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
                    <div className="bg-white p-7 space-y-6">
                        <div>
                            <label className="label-line mb-3">Aktuelle Phase</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {(data?.phases || []).map((p) => (
                                    <button
                                        key={p.value}
                                        onClick={() => setPhaseAndDefault(p.value)}
                                        className={`text-left px-4 py-3 border transition-all font-body text-sm ${
                                            phase === p.value
                                                ? "bg-ink border-ink text-white"
                                                : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
                                        }`}
                                        data-testid={`admin-phase-btn-${p.value}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <p className="font-body text-[11px] text-stone-500 mt-2">
                                Tipp: Phasen-Auswahl setzt einen empfohlenen Fortschritt – Sie können den Slider danach individuell anpassen.
                            </p>
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between">
                                <label className="label-line">Fortschritt der Phase</label>
                                <span className="font-heading text-2xl text-ink">{progress}%</span>
                            </div>
                            <input
                                type="range"
                                min="0" max="100" step="5"
                                value={progress}
                                onChange={(e) => setProgress(parseInt(e.target.value, 10))}
                                className="w-full mt-3 accent-brand-500"
                                data-testid="construction-progress-slider"
                            />
                        </div>

                        <div>
                            <label className="label-line mb-2">Kurzbeschreibung (optional)</label>
                            <textarea
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-3 bg-cream border border-stone-200 focus:border-brand-500 focus:outline-none font-body text-sm"
                                placeholder="z. B. „Bodenplatte fertiggestellt – Rohbau startet kommende Woche"
                                data-testid="construction-description-input"
                            />
                        </div>

                        <button
                            onClick={save}
                            disabled={saving}
                            className="btn-primary disabled:opacity-60"
                            data-testid="construction-save"
                        >
                            {saving ? (
                                <><Loader2 size={14} className="animate-spin" /> Speichere…</>
                            ) : (
                                <><Save size={14} /> Speichern &amp; live stellen</>
                            )}
                        </button>
                    </div>

                    {/* Live preview */}
                    <div className="bg-ink text-white p-7" data-testid="construction-preview">
                        <div className="flex items-center gap-2 mb-3">
                            <Activity size={16} className="text-clay-300" />
                            <span className="overline text-clay-300">Vorschau (Public)</span>
                        </div>
                        <h3 className="font-heading text-2xl text-white">
                            {(data?.phases || []).find((p) => p.value === phase)?.label}
                        </h3>
                        {description && (
                            <p className="font-body text-sm text-white/70 mt-2 leading-relaxed">{description}</p>
                        )}
                        <div className="mt-6">
                            <div className="flex items-baseline justify-between mb-1">
                                <span className="overline text-white/60">Fortschritt</span>
                                <span className="font-heading text-xl text-clay-300">{progress}%</span>
                            </div>
                            <div className="h-1.5 bg-white/10">
                                <div className="h-full bg-clay-300 transition-all duration-500" style={{ width: `${progress}%` }} />
                            </div>
                        </div>
                        {data?.updated_at && (
                            <p className="font-body text-[11px] text-white/40 mt-6">
                                Stand zuletzt: {new Date(data.updated_at).toLocaleString("de-DE")}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
